import React from 'react'

import { Currency } from '@toasttab/buffet-pui-number-utilities'
import { Restaurant } from 'schema-dts'

import { I18nType } from 'src/lib/js/hooks/useFormatPrice'
import { formatImageURL } from 'src/lib/js/utilities'

import { useBulkCalls } from 'shared/components/common/dynamic_menus/BulkCallsContext'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useLocationSchedules } from 'public/components/online_ordering/FulfillmentContext'
import { buildBaseRestaurantSchema } from 'public/components/seo/restaurantSchemaBuilder'
import SchemaThing from 'public/components/seo/SchemaThing'

type Props = {
  rxGuid?: string
}

const DynamicMenuPageSchema = ({ rxGuid }: Props) => {
  const { restaurant: site, locations, ooRestaurant } = useRestaurant()
  const locationSchedules = useLocationSchedules()
  const { menuGroups, menuItems } = useBulkCalls()

  const selectedLocation = locations?.find(
    (location) => location.externalId === rxGuid
  )
  const locationSchedule =
    locationSchedules && selectedLocation?.externalId
      ? locationSchedules[selectedLocation?.externalId]
      : undefined
  const restaurantI18n: I18nType | undefined = ooRestaurant?.i18n as
    | I18nType
    | undefined

  if (!selectedLocation || !selectedLocation.name || !ooRestaurant) {
    return null
  }
  return (
    <SchemaThing<Restaurant>
      json={(routes) => ({
        ...buildBaseRestaurantSchema({
          restaurantName: selectedLocation.name || '',
          restaurantPhone: ooRestaurant?.location?.phone,
          country: restaurantI18n?.country,
          currency: restaurantI18n?.currency,
          selectedLocation,
          locationSchedule,
          site,
          locationUrl: routes.location,
          homeUrl: routes.homeUrl,
          orderUrl: selectedLocation?.shortUrl
            ? routes.orderUrl(rxGuid || '')
            : undefined,
          lastPublishedAt: site.lastPublishedAt,
          googlePlaceId: ooRestaurant?.googlePlaceId,
          giftCardPurchaseUrl: ooRestaurant?.giftCardLinks?.purchaseLink
        }),
        hasMenu: {
          '@type': 'Menu',
          '@id': `${routes.location}`,
          hasMenuSection: menuGroups?.map((menuGroup) => ({
            '@type': 'MenuSection',
            '@id': menuGroup?.guid,
            name: menuGroup?.name,
            description: menuGroup?.description,
            hasMenuItem: menuGroup?.items?.map((menuItem) =>
              getMenuItemSchema(menuItem, restaurantI18n?.currency)
            )
          })),
          hasMenuItem: menuItems?.map((menuItem) =>
            getMenuItemSchema(menuItem, restaurantI18n?.currency)
          )
        }
      })}
    />
  )
}

const getMenuItemSchema = (menuItem: any, currency?: Currency) => {
  return {
    '@type': 'MenuItem',
    '@id': menuItem?.guid,
    identifier: menuItem?.guid,
    name: menuItem?.name,
    description: menuItem?.description,
    image: menuItem?.imageLink
      ? formatImageURL(menuItem?.imageLink)
      : undefined,
    offers: menuItem.price
      ? {
          '@type': 'OfferForPurchase',
          price: menuItem.price,
          priceCurrency: currency
        }
      : undefined
  }
}

export default DynamicMenuPageSchema
